import { graphql } from '../../gql';

/**
 * Lightweight composition V3 query (headcount only).
 * Used on overview pages where only the headcount line chart is needed.
 * `breakdown_options` is required by the API even when breakdown payloads are unused.
 */
export const COMPOSITION_HEADCOUNT_ONLY_V3 = graphql(`
  query CompositionHeadcountOnlyV3($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
        metrics {
          headcount {
            id
            val
          }
        }
      }
    }
  }
`);

export const COMPOSITION_SKILL_BREAKDOWN_V3 = graphql(`
  query CompositionSkillBreakdownV3($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
        metrics {
          headcount {
            id
            val
          }
          skills {
            id
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
        }
      }
    }
  }
`);

export const COMPOSITION_ACTIVITY_BREAKDOWN_V3 = graphql(`
  query CompositionActivityBreakdownV3($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
        metrics {
          headcount {
            id
            val
          }
          activities {
            id
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
        }
      }
    }
  }
`);

export const COMPOSITION_GET_DATA_V3 = graphql(`
  query CompositionV3Data($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
        metrics {
          headcount {
            id
            val
          }
          tenure {
            id
            val
          }
          salary {
            id
            val
          }
          growth_rate {
            id
            val
          }
          hiring_rate {
            id
            val
          }
          attrition_rate {
            id
            val
          }
          gender {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          ethnicity {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          role {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          geography {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          seniority {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          education {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          rics {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          skills {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
        }
      }
    }
  }
`);

export const COMPOSITION_GET_DATA_WITH_ACTIVITIES_V3 = graphql(`
  query CompositionV3DataWithActivities($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
        metrics {
          headcount {
            id
            val
          }
          tenure {
            id
            val
          }
          salary {
            id
            val
          }
          growth_rate {
            id
            val
          }
          hiring_rate {
            id
            val
          }
          attrition_rate {
            id
            val
          }
          gender {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          ethnicity {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          role {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          geography {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          seniority {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          education {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          rics {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          skills {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
          activities {
            id
            rank
            short_name
            long_name
            headcount {
              id
              count
              share
            }
          }
        }
      }
    }
  }
`);

/**
 * Metadata-only composition V3 query: resolves entity id / short_name /
 * long_name without fetching metrics, so entity search and AI-search
 * company-name resolution don't over-fetch the full breakdown payload.
 * `breakdown_options` is required by the API even when unused.
 */
export const COMPOSITION_ENTITY_DATA_V3 = graphql(`
  query CompositionV3EntityData($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
      }
    }
  }
`);

/** Snapshot-style tooltip: headcount + growth rates + salary timeseries for a single month range. */
export const COMPOSITION_V3_TOOLTIP = graphql(`
  query CompositionV3Tooltip($req: CompositionV3Req!) {
    composition_v3(req: $req) {
      entities {
        id
        short_name
        long_name
        metrics {
          headcount {
            id
            val
          }
          growth_rate {
            id
            val
          }
          salary {
            id
            val
          }
        }
      }
    }
  }
`);
